/**
 * MultidevCollaborationProvider
 * Task 5.7: SDK Entry Point
 *
 * Context provider for SDK state management.
 * Manages comments, team members, API client, and polling.
 *
 * Features:
 * - Centralized state for comments and team members
 * - API client initialization
 * - Polling for comment updates
 * - Comment CRUD operations
 * - Element matching integration
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import type { Comment, TeamMember, MatchResult, ConfidenceLevel, JiraTicket, JiraFormFields, GeneratedTicketContent, JiraProject, JiraIssueType } from '../lib/types';
import type { JiraConnectionResponse, CommentJiraTicket } from '../lib/api-client';
import { ApiClient } from '../lib/api-client';
import { findMatch } from '../lib/matching';
import { captureFingerprint } from '../lib/fingerprint';
import { useAuthManagerOptional } from './AuthManagerContext';

export interface SDKConfig {
  siteId: string;
  environmentId: string;
  apiUrl: string;
}

interface CommentWithMatch {
  comment: Comment;
  matchResult: MatchResult | null;
  confidence: ConfidenceLevel;
}

interface MultidevCollaborationContextValue {
  // Configuration
  config: SDKConfig;

  // State
  comments: Comment[];
  teamMembers: TeamMember[];
  commentsWithMatches: CommentWithMatch[];
  orphanedCommentIds: string[];
  isLoading: boolean;
  error: string | null;
  hasNewComments: boolean;
  /** Set of parent comment IDs that are unread for the current user */
  unreadCommentIds: Set<string>;

  // Current page
  currentPagePath: string;

  // Actions
  createComment: (content: string, element: Element) => Promise<Comment | null>;
  createReply: (content: string, parentComment: Comment) => Promise<Comment | null>;
  updateComment: (id: string, content: string) => Promise<Comment | null>;
  deleteComment: (id: string) => Promise<boolean>;
  resolveComment: (id: string) => Promise<boolean>;
  reopenComment: (id: string) => Promise<boolean>;
  reAnchorComment: (id: string, element: Element) => Promise<boolean>;
  refreshComments: () => Promise<void>;
  /** Marks a comment as read (optimistic update + API call) */
  markAsRead: (commentId: string) => void;
  /** Marks a comment as unread (optimistic update + API call) */
  markAsUnread: (commentId: string) => void;

  // Helpers
  getAuthor: (authorId: string) => TeamMember | undefined;

  // Jira Integration
  jiraConnection: JiraConnectionResponse | null;
  jiraTickets: Map<string, CommentJiraTicket>;
  jiraProjects: JiraProject[];
  jiraIssueTypes: JiraIssueType[];
  jiraProjectsLoading: boolean;
  jiraIssueTypesLoading: boolean;
  generateJiraTicket: (comment: Comment) => Promise<GeneratedTicketContent>;
  createJiraTicket: (comment: Comment, fields: JiraFormFields) => Promise<JiraTicket>;
  getCommentJiraTicket: (commentId: string) => Promise<CommentJiraTicket | null>;
  fetchJiraProjects: () => Promise<void>;
  fetchJiraIssueTypes: (projectKey: string) => Promise<void>;
  connectJira: () => void;
}

const MultidevCollaborationContext = createContext<MultidevCollaborationContextValue | null>(null);

export function useMultidevCollaboration(): MultidevCollaborationContextValue {
  const context = useContext(MultidevCollaborationContext);
  if (!context) {
    throw new Error('useMultidevCollaboration must be used within MultidevCollaborationProvider');
  }
  return context;
}

interface MultidevCollaborationProviderProps {
  config: SDKConfig;
  children: React.ReactNode;
}

const POLLING_INTERVAL = 30000; // 30 seconds

// Mock Jira connection for development/testing
const MOCK_JIRA_CONNECTION: JiraConnectionResponse = {
  id: 'mock-connection-1',
  siteId: 'mock-site-1',
  jiraCloudId: 'mock-cloud-1',
  jiraSiteUrl: 'https://example.atlassian.net',
  defaultProjectKey: 'PROJ',
  defaultIssueType: 'Bug',
  connectedBy: 'mock-user-1',
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
};

export const MultidevCollaborationProvider: React.FC<MultidevCollaborationProviderProps> = ({
  config,
  children,
}) => {
  // State
  const [comments, setComments] = useState<Comment[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasNewComments, setHasNewComments] = useState(false);
  const [lastPollTimestamp, setLastPollTimestamp] = useState<string | null>(null);

  // Jira state
  const [jiraConnection, setJiraConnection] = useState<JiraConnectionResponse | null>(null);
  const [jiraTickets, setJiraTickets] = useState<Map<string, CommentJiraTicket>>(new Map());
  const [jiraProjects, setJiraProjects] = useState<JiraProject[]>([]);
  const [jiraIssueTypes, setJiraIssueTypes] = useState<JiraIssueType[]>([]);
  const [jiraProjectsLoading, setJiraProjectsLoading] = useState(false);
  const [jiraIssueTypesLoading, setJiraIssueTypesLoading] = useState(false);

  // Refs
  const apiClientRef = useRef<ApiClient | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Current page path
  const currentPagePath = useMemo(() => window.location.pathname, []);

  // Get auth context (optional - may be null when auth is disabled)
  const authContext = useAuthManagerOptional();
  const idToken = authContext?.idToken ?? null;

  // Initialize API client
  useEffect(() => {
    apiClientRef.current = new ApiClient({
      baseUrl: config.apiUrl,
      siteId: config.siteId,
      environmentId: config.environmentId,
      authToken: idToken ?? undefined,  // Set id_token for auth (hackweek - TODO: upgrade to MAS token)
    });
  }, [config, idToken]);

  // Update API client auth token when it changes
  useEffect(() => {
    if (apiClientRef.current && idToken) {
      apiClientRef.current.setAuthToken(idToken);
    }
  }, [idToken]);

  // Team members map for quick lookup
  const teamMembersMap = useMemo(
    () => new Map(teamMembers.map(m => [m.id, m])),
    [teamMembers]
  );

  // Get author helper
  const getAuthor = useCallback((authorId: string): TeamMember | undefined => {
    return teamMembersMap.get(authorId);
  }, [teamMembersMap]);

  // Match comments to elements
  const commentsWithMatches = useMemo((): CommentWithMatch[] => {
    // Only match parent comments (not replies)
    const parentComments = comments.filter(c => c.threadId === null);

    return parentComments.map(comment => {
      const matchResult = findMatch(comment.elementFingerprint);

      let confidence: ConfidenceLevel = 'orphaned';
      if (matchResult) {
        if (matchResult.confidence >= 0.9) {
          confidence = 'high';
        } else if (matchResult.confidence >= 0.7) {
          confidence = 'medium';
        } else if (matchResult.confidence >= 0.5) {
          confidence = 'low';
        }
      }

      return {
        comment,
        matchResult,
        confidence,
      };
    });
  }, [comments]);

  // Orphaned comment IDs
  const orphanedCommentIds = useMemo(
    () => commentsWithMatches
      .filter(cwm => cwm.confidence === 'orphaned')
      .map(cwm => cwm.comment.id),
    [commentsWithMatches]
  );

  // Unread comment IDs (computed from isUnread field on parent comments)
  const unreadCommentIds = useMemo(
    () => new Set(
      comments
        .filter(c => c.threadId === null && c.isUnread === true)
        .map(c => c.id)
    ),
    [comments]
  );

  // Mark comment as read (optimistic update + API call)
  // Requirements: docs/specs/011526_track-comment-reads/requirements.md FR-3
  const markAsRead = useCallback((commentId: string) => {
    // Skip if not in unread set (already read or doesn't exist)
    if (!unreadCommentIds.has(commentId)) {
      return;
    }

    // Optimistic update: set isUnread to false
    setComments(prev => prev.map(c =>
      c.id === commentId ? { ...c, isUnread: false } : c
    ));

    // Background API call - update with returned comment
    if (apiClientRef.current) {
      apiClientRef.current.markAsRead(commentId)
        .then(updatedComment => {
          // Sync server state
          setComments(prev => prev.map(c =>
            c.id === commentId ? updatedComment : c
          ));
        })
        .catch(err => {
          console.warn('[Multidev Collaboration] Failed to mark as read:', err);
          // Revert optimistic update on error
          setComments(prev => prev.map(c =>
            c.id === commentId ? { ...c, isUnread: true } : c
          ));
        });
    }
  }, [unreadCommentIds]);

  // Mark comment as unread (optimistic update + API call)
  // Requirements: docs/specs/011526_track-comment-reads/requirements.md FR-10
  const markAsUnread = useCallback((commentId: string) => {
    // Skip if already in unread set
    if (unreadCommentIds.has(commentId)) {
      return;
    }

    // Optimistic update: set isUnread to true
    setComments(prev => prev.map(c =>
      c.id === commentId ? { ...c, isUnread: true } : c
    ));

    // Background API call - update with returned comment
    if (apiClientRef.current) {
      apiClientRef.current.markAsUnread(commentId)
        .then(updatedComment => {
          // Sync server state
          setComments(prev => prev.map(c =>
            c.id === commentId ? updatedComment : c
          ));
        })
        .catch(err => {
          console.warn('[Multidev Collaboration] Failed to mark as unread:', err);
          // Revert optimistic update on error
          setComments(prev => prev.map(c =>
            c.id === commentId ? { ...c, isUnread: false } : c
          ));
        });
    }
  }, [unreadCommentIds]);

  // Fetch initial data
  const fetchInitialData = useCallback(async () => {
    if (!apiClientRef.current) return;

    setIsLoading(true);
    setError(null);

    try {
      // Fetch comments (required) and team members (optional, for @mentions)
      const [commentsResult, teamMembersResult] = await Promise.allSettled([
        apiClientRef.current.getComments(),
        apiClientRef.current.getTeamMembers(),
      ]);

      // Handle comments - required
      if (commentsResult.status === 'fulfilled') {
        setComments(commentsResult.value);
      } else {
        throw commentsResult.reason;
      }

      // Handle team members - optional (endpoint may not exist yet)
      if (teamMembersResult.status === 'fulfilled') {
        setTeamMembers(teamMembersResult.value);
      } else {
        console.warn('[Multidev Collaboration] Team members endpoint not available:', teamMembersResult.reason);
        setTeamMembers([]);
      }

      setLastPollTimestamp(new Date().toISOString());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load comments');
      console.error('[Multidev Collaboration] Failed to fetch initial data:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Store lastPollTimestamp in a ref to avoid recreating pollForUpdates
  const lastPollTimestampRef = useRef<string | null>(null);
  useEffect(() => {
    lastPollTimestampRef.current = lastPollTimestamp;
  }, [lastPollTimestamp]);

  // Poll for updates - stable callback using ref
  const pollForUpdates = useCallback(async () => {
    if (!apiClientRef.current || !lastPollTimestampRef.current) return;

    try {
      const newComments = await apiClientRef.current.pollComments(lastPollTimestampRef.current);

      if (newComments.length > 0) {
        setHasNewComments(true);

        // Merge new comments
        setComments(prev => {
          const updatedComments = [...prev];

          for (const newComment of newComments) {
            const existingIndex = updatedComments.findIndex(c => c.id === newComment.id);
            if (existingIndex >= 0) {
              updatedComments[existingIndex] = newComment;
            } else {
              updatedComments.push(newComment);
            }
          }

          return updatedComments;
        });
      }

      setLastPollTimestamp(new Date().toISOString());
    } catch (err) {
      console.error('[Multidev Collaboration] Polling error:', err);
    }
  }, []);

  // Set up polling - only runs once on mount
  // Wait for auth to complete before fetching data to ensure cookies are set
  useEffect(() => {
    // Skip if auth is still initializing (prevents 401 errors after auth redirect)
    if (authContext && authContext.isLoading) {
      return;
    }

    fetchInitialData();

    pollingIntervalRef.current = setInterval(pollForUpdates, POLLING_INTERVAL);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [fetchInitialData, pollForUpdates, authContext?.isLoading]);

  // Refresh comments
  const refreshComments = useCallback(async () => {
    setHasNewComments(false);
    await fetchInitialData();
  }, [fetchInitialData]);

  // Create comment
  const createComment = useCallback(async (
    content: string,
    element: Element
  ): Promise<Comment | null> => {
    if (!apiClientRef.current) return null;

    try {
      const fingerprint = captureFingerprint(element);

      const newComment = await apiClientRef.current.createComment({
        pagePath: currentPagePath,
        elementFingerprint: fingerprint,
        content,
        type: 'review_note',
      });

      setComments(prev => [...prev, newComment]);
      return newComment;
    } catch (err) {
      console.error('[Multidev Collaboration] Failed to create comment:', err);
      return null;
    }
  }, [currentPagePath]);

  // Create reply to an existing comment thread
  const createReply = useCallback(async (
    content: string,
    parentComment: Comment
  ): Promise<Comment | null> => {
    if (!apiClientRef.current) return null;

    try {
      const newReply = await apiClientRef.current.createComment({
        pagePath: parentComment.pagePath,
        elementFingerprint: parentComment.elementFingerprint,
        content,
        type: 'review_note',
        threadId: parentComment.id,
      });

      setComments(prev => [...prev, newReply]);
      return newReply;
    } catch (err) {
      console.error('[Multidev Collaboration] Failed to create reply:', err);
      return null;
    }
  }, []);

  // Update comment
  const updateComment = useCallback(async (
    id: string,
    content: string
  ): Promise<Comment | null> => {
    if (!apiClientRef.current) return null;

    try {
      const updated = await apiClientRef.current.updateComment(id, { content });

      setComments(prev => prev.map(c => c.id === id ? updated : c));
      return updated;
    } catch (err) {
      console.error('[Multidev Collaboration] Failed to update comment:', err);
      return null;
    }
  }, []);

  // Delete comment
  const deleteComment = useCallback(async (id: string): Promise<boolean> => {
    if (!apiClientRef.current) return false;

    try {
      await apiClientRef.current.deleteComment(id);
      setComments(prev => prev.filter(c => c.id !== id && c.threadId !== id));
      return true;
    } catch (err) {
      console.error('[Multidev Collaboration] Failed to delete comment:', err);
      return false;
    }
  }, []);

  // Resolve comment
  const resolveComment = useCallback(async (id: string): Promise<boolean> => {
    if (!apiClientRef.current) return false;

    try {
      await apiClientRef.current.resolveComment(id);
      setComments(prev => prev.map(c =>
        c.id === id ? { ...c, status: 'resolved' as const, resolvedAt: new Date().toISOString() } : c
      ));
      return true;
    } catch (err) {
      console.error('[Multidev Collaboration] Failed to resolve comment:', err);
      return false;
    }
  }, []);

  // Reopen comment
  const reopenComment = useCallback(async (id: string): Promise<boolean> => {
    if (!apiClientRef.current) return false;

    try {
      await apiClientRef.current.reopenComment(id);
      setComments(prev => prev.map(c =>
        c.id === id ? { ...c, status: 'open' as const, resolvedAt: null } : c
      ));
      return true;
    } catch (err) {
      console.error('[Multidev Collaboration] Failed to reopen comment:', err);
      return false;
    }
  }, []);

  // Re-anchor comment
  const reAnchorComment = useCallback(async (
    id: string,
    element: Element
  ): Promise<boolean> => {
    if (!apiClientRef.current) return false;

    try {
      const fingerprint = captureFingerprint(element);
      await apiClientRef.current.reAnchorComment(id, fingerprint);

      setComments(prev => prev.map(c =>
        c.id === id ? { ...c, elementFingerprint: fingerprint } : c
      ));
      return true;
    } catch (err) {
      console.error('[Multidev Collaboration] Failed to re-anchor comment:', err);
      return false;
    }
  }, []);

  // ============================================================================
  // Jira Integration
  // ============================================================================

  // Load Jira connection on mount
  useEffect(() => {
    const loadJiraConnection = async () => {
      if (!apiClientRef.current) {
        // Use mock connection for development
        if (process.env.NODE_ENV !== 'production') {
          console.log('[Multidev Collaboration] Using mock Jira connection for development');
        }
        setJiraConnection(MOCK_JIRA_CONNECTION);
        return;
      }

      try {
        const conn = await apiClientRef.current.getJiraConnection();
        if (conn) {
          setJiraConnection(conn);
          if (process.env.NODE_ENV !== 'production') {
            console.log('[Multidev Collaboration] Jira connected:', conn.jiraSiteUrl);
          }
        } else {
          // No connection exists - leave as null so UI shows connection prompt
          if (process.env.NODE_ENV !== 'production') {
            console.log('[Multidev Collaboration] No Jira connection found');
          }
          setJiraConnection(null);
        }
      } catch (err) {
        console.warn('[Multidev Collaboration] Failed to load Jira connection:', err);
        // Leave as null on error
        setJiraConnection(null);
      }
    };

    // Delay to ensure apiClient is initialized
    const timer = setTimeout(loadJiraConnection, 100);
    return () => clearTimeout(timer);
  }, [config]);

  // Fetch Jira projects on-demand (called when Jira form opens)
  const fetchJiraProjects = useCallback(async (): Promise<void> => {
    if (!apiClientRef.current || !jiraConnection) {
      console.warn('[Multidev Collaboration] No API client or connection for fetching projects');
      return;
    }

    // Skip if already loaded
    if (jiraProjects.length > 0) {
      return;
    }

    setJiraProjectsLoading(true);
    try {
      const projects = await apiClientRef.current.getJiraProjects();
      setJiraProjects(projects);
      if (process.env.NODE_ENV !== 'production') {
        console.log('[Multidev Collaboration] Loaded Jira projects:', projects.length);
      }

      // Auto-load issue types for the first/default project
      if (projects.length > 0) {
        const targetProject = jiraConnection.defaultProjectKey
          ? projects.find(p => p.key === jiraConnection.defaultProjectKey)
          : projects[0];
        if (targetProject) {
          const issueTypes = await apiClientRef.current.getJiraIssueTypes(targetProject.key);
          setJiraIssueTypes(issueTypes);
          if (process.env.NODE_ENV !== 'production') {
            console.log('[Multidev Collaboration] Loaded issue types for project:', targetProject.key, issueTypes.length);
          }
        }
      }
    } catch (err) {
      console.warn('[Multidev Collaboration] Failed to load Jira projects:', err);
      setJiraProjects([]);
    } finally {
      setJiraProjectsLoading(false);
    }
  }, [jiraConnection, jiraProjects.length]);

  // Fetch issue types for a specific project
  const fetchJiraIssueTypes = useCallback(async (projectKey: string): Promise<void> => {
    if (!apiClientRef.current) {
      console.warn('[Multidev Collaboration] No API client for fetching issue types');
      return;
    }

    setJiraIssueTypesLoading(true);
    try {
      const issueTypes = await apiClientRef.current.getJiraIssueTypes(projectKey);
      setJiraIssueTypes(issueTypes);
      if (process.env.NODE_ENV !== 'production') {
        console.log('[Multidev Collaboration] Loaded issue types for project:', projectKey, issueTypes.length);
      }
    } catch (err) {
      console.warn('[Multidev Collaboration] Failed to load issue types:', err);
      setJiraIssueTypes([]);
    } finally {
      setJiraIssueTypesLoading(false);
    }
  }, []);

  // Generate Jira ticket content with AI
  const generateJiraTicket = useCallback(async (comment: Comment): Promise<GeneratedTicketContent> => {
    if (!apiClientRef.current) {
      // Mock implementation for development
      if (process.env.NODE_ENV !== 'production') {
        console.log('[Multidev Collaboration] Mock: Generating ticket for comment:', comment.id);
      }
      await new Promise(resolve => setTimeout(resolve, 1500)); // Simulate API delay
      return {
        summary: `Issue on ${comment.pagePath}: ${comment.content.slice(0, 50)}...`,
        description: `## Issue\n${comment.content}\n\n## Location\n- **Page:** ${comment.pagePath}\n- **Comment ID:** ${comment.id}\n\n## Acceptance Criteria\n- [ ] Issue addressed\n- [ ] Verified in review`,
      };
    }

    try {
      const result = await apiClientRef.current.generateJiraTicket(comment.id);
      return result;
    } catch (err) {
      console.warn('[Multidev Collaboration] API generateJiraTicket failed, using mock:', err);
      // Fall back to mock
      return {
        summary: `Issue on ${comment.pagePath}: ${comment.content.slice(0, 50)}...`,
        description: `## Issue\n${comment.content}\n\n## Location\n- **Page:** ${comment.pagePath}\n\n## Acceptance Criteria\n- [ ] Issue addressed`,
      };
    }
  }, []);

  // Create Jira ticket
  const createJiraTicket = useCallback(async (
    comment: Comment,
    fields: JiraFormFields
  ): Promise<JiraTicket> => {
    if (!apiClientRef.current) {
      // Mock implementation for development
      if (process.env.NODE_ENV !== 'production') {
        console.log('[Multidev Collaboration] Mock: Creating ticket for comment:', comment.id, fields);
      }
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API delay

      const mockTicket: JiraTicket = {
        key: `${fields.projectKey || 'PROJ'}-${Math.floor(Math.random() * 9999) + 1}`,
        id: `mock-${Date.now()}`,
        url: `https://example.atlassian.net/browse/${fields.projectKey || 'PROJ'}-${Math.floor(Math.random() * 9999) + 1}`,
        projectKey: fields.projectKey || 'PROJ',
        issueType: 'Bug',
        createdAt: new Date().toISOString(),
        createdBy: 'mock-user-1',
      };

      // Store the ticket
      setJiraTickets(prev => {
        const newMap = new Map(prev);
        newMap.set(comment.id, {
          id: mockTicket.id,
          commentId: comment.id,
          jiraIssueKey: mockTicket.key,
          jiraIssueUrl: mockTicket.url,
          jiraProjectKey: mockTicket.projectKey,
          createdBy: mockTicket.createdBy,
          createdAt: mockTicket.createdAt,
        });
        return newMap;
      });

      return mockTicket;
    }

    try {
      // Use effective values - fall back to first available if field is empty
      const effectiveProjectKey = fields.projectKey || jiraProjects[0]?.key || '';
      const effectiveIssueType = fields.issueTypeId ||
        jiraIssueTypes.find(t => t.name.toLowerCase() === 'task')?.id ||
        jiraIssueTypes[0]?.id || '';

      const result = await apiClientRef.current.createJiraTicket({
        commentId: comment.id,
        summary: fields.summary,
        description: fields.description,
        projectKey: effectiveProjectKey,
        issueType: effectiveIssueType,
      });

      const ticket: JiraTicket = {
        key: result.ticketKey,
        id: result.ticketKey, // Use key as ID for now
        url: result.ticketUrl,
        projectKey: fields.projectKey || '',
        issueType: fields.issueTypeId || 'Bug',
        createdAt: new Date().toISOString(),
        createdBy: '', // Would come from user context
      };

      // Store the ticket
      setJiraTickets(prev => {
        const newMap = new Map(prev);
        newMap.set(comment.id, {
          id: ticket.id,
          commentId: comment.id,
          jiraIssueKey: ticket.key,
          jiraIssueUrl: ticket.url,
          jiraProjectKey: ticket.projectKey,
          createdBy: ticket.createdBy,
          createdAt: ticket.createdAt,
        });
        return newMap;
      });

      return ticket;
    } catch (err) {
      console.error('[Multidev Collaboration] Failed to create Jira ticket:', err);
      throw err;
    }
  }, []);

  // Get linked ticket for a comment
  const getCommentJiraTicket = useCallback(async (commentId: string): Promise<CommentJiraTicket | null> => {
    // Check cache first
    const cached = jiraTickets.get(commentId);
    if (cached) return cached;

    if (!apiClientRef.current) {
      return null;
    }

    try {
      const ticket = await apiClientRef.current.getCommentJiraTicket(commentId);
      if (ticket) {
        setJiraTickets(prev => {
          const newMap = new Map(prev);
          newMap.set(commentId, ticket);
          return newMap;
        });
      }
      return ticket;
    } catch (err) {
      console.warn('[Multidev Collaboration] Failed to get comment ticket:', err);
      return null;
    }
  }, [jiraTickets]);

  // Initiate Jira OAuth connection
  const connectJira = useCallback(() => {
    if (!apiClientRef.current) {
      console.warn('[Multidev Collaboration] No API client available for Jira OAuth');
      return;
    }

    // Get the OAuth URL with current page as return URL
    const returnUrl = window.location.href;
    const oauthUrl = apiClientRef.current.getJiraOAuthUrl(returnUrl);

    if (process.env.NODE_ENV !== 'production') {
      console.log('[Multidev Collaboration] Initiating Jira OAuth:', oauthUrl);
    }

    // Navigate to the OAuth URL
    window.location.href = oauthUrl;
  }, []);

  const value: MultidevCollaborationContextValue = {
    config,
    comments,
    teamMembers,
    commentsWithMatches,
    orphanedCommentIds,
    isLoading,
    error,
    hasNewComments,
    unreadCommentIds,
    currentPagePath,
    createComment,
    createReply,
    updateComment,
    deleteComment,
    resolveComment,
    reopenComment,
    reAnchorComment,
    refreshComments,
    markAsRead,
    markAsUnread,
    getAuthor,
    // Jira Integration
    jiraConnection,
    jiraTickets,
    jiraProjects,
    jiraIssueTypes,
    jiraProjectsLoading,
    jiraIssueTypesLoading,
    generateJiraTicket,
    createJiraTicket,
    getCommentJiraTicket,
    fetchJiraProjects,
    fetchJiraIssueTypes,
    connectJira,
  };

  return (
    <MultidevCollaborationContext.Provider value={value}>
      {children}
    </MultidevCollaborationContext.Provider>
  );
};
